import React from "react";
import { hexForColorName, isValidHex, isLightHex } from "../../lib/colorHex";

/**
 * Generic colour / shade picker that plugs into any product with
 * `color`-defining variants (foundation, concealer, apparel, etc).
 *
 * Renders swatches for the unique `color` values seen across
 * `product.variants`. Each entry may pin an explicit `color_hex`
 * (set by the admin on the variant); otherwise we fall back to the
 * shared `hexForColorName` map, and finally a text-chip when the
 * shade name is brand-specific.
 *
 * The picker keeps the surface tiny — no separate schema, no shade
 * catalogue. It ONLY reads what the tenant admin already entered on
 * the variant matrix.
 */

/**
 * @param {{ name: string, hex?: string|null, disabled?: boolean }[]} colors
 * @param {string}   value    currently selected colour name.
 * @param {function} onChange (name) => void
 * @param {string}   label    optional heading label. Defaults "Select Shade".
 */
export default function ColorSwatchPicker({
  colors = [],
  value,
  onChange,
  label = "Select Shade",
}) {
  if (!colors.length) return null;
  return (
    <div className="mb-6" data-testid="pdp-color-picker">
      <div className="overline mb-3">{label}</div>
      <div className="flex flex-wrap gap-2 items-center">
        {colors.map(({ name, hex, disabled }) => {
          const pinned = isValidHex(hex) ? hex : null;
          const resolved = pinned || hexForColorName(name);
          const selected = value === name;
          const light = resolved ? isLightHex(resolved) : false;
          // When the "name" IS the hex (unnamed swatch-only shades),
          // hide the redundant text label — the swatch alone tells
          // the shopper everything.
          const showLabel = !/^#[0-9A-Fa-f]{6}$/.test(name || "");
          return (
            <button
              key={name}
              type="button"
              disabled={disabled}
              onClick={() => onChange(name)}
              aria-pressed={selected}
              aria-label={name}
              title={name + (disabled ? " (out of stock)" : "")}
              data-testid={`color-swatch-${name}`}
              className={`group relative flex items-center gap-2 rounded-full border transition ${
                showLabel ? "px-2 py-1" : "p-1"
              } ${
                selected
                  ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/25"
                  : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/60"
              } ${disabled ? "opacity-40 cursor-not-allowed line-through" : ""}`}
            >
              <span
                className={`${showLabel ? "w-6 h-6" : "w-8 h-8"} rounded-full border ${
                  light ? "border-[hsl(var(--kb-border))]" : "border-black/10"
                }`}
                style={{ background: resolved || "hsl(var(--kb-surface-alt))" }}
                aria-hidden="true"
              />
              {showLabel && <span className="text-sm pr-2">{name}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
